import { FaCity } from "react-icons/fa";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useRouter } from "@/app/hooks/useRouter";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useConfigurationModuleSearchAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationModuleSearchAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { getCityColumns } from "@/app/hooks/custom_hooks";

const ConfigurationCitySetup = () => {
  const hasFetchedRef = useRef(false);
  const { getRouteKey } = useRouter();
  const isCityListInProgress = useLoader("isOnboardCityListInProgress");
  const navigate = useNavigate();
  const fetchCityList = useConfigurationCityListAPI();
  const routeIdConfig = getRouteKey("CONFIGURATION", "id");
  const { routeState } = useRouteInfo(routeIdConfig);
  const { cityList: cityListData } = routeState.state as IConfigurationListState;
  const searchCity = useConfigurationModuleSearchAPI("city");
  const columns = getCityColumns(navigate, getRouteKey);
  const { NO_APPOINTMENTS } = useImageConstants();

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchCityList();
      hasFetchedRef.current = true;
    }
  }, []);

  return (
    <div className="px-5 py-3">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="font-semibold text-base text-gray-900 leading-6">Cities</h1>
          <p className="mt-2 text-gray-700 text-sm">A list of all the cities onboarded in the VMS.</p>
        </div>
        <div className="sm:flex-none mt-4 sm:mt-0 sm:ml-16">
          <SolidButton size="XL" onButtonClick={() => navigate(getRouteKey("CITY_SETUP_ONBOARD", "url"))}>
            Onboard New City
          </SolidButton>
        </div>
      </div>
      <div>
        <InputWithAddOn
          onEnterKeyPress={(query: string) => {
            if (query.length === 0) {
              fetchCityList();
            } else {
              searchCity(query);
            }
          }}
          containerClassName="w-1/2"
          addon={<FaCity className="w-4 h-4" />}
          placeholder="Search City"
        />
      </div>
      {isCityListInProgress ? (
        <div className="mt-4">
          <SkeletonType1 />
          <SkeletonType1 className="mt-8" />
        </div>
      ) : cityListData?.length > 0 ? (
        <div>
          <DataTable data={cityListData || []} columns={columns} maxHeight="600px" />
        </div>
      ) : (
        <NoDataFoundComponent
          imageURL={NO_APPOINTMENTS}
          message="No cities have been onboarded"
          description="No city data is available try onboarding a city"
        />
      )}
    </div>
  );
};

export default ConfigurationCitySetup;
